import React from 'react';

import { useActionDocumentTemplate } from '~/components/AiChat/hooks/useActionDocumentTemplate';
import type { AiAction } from '~/components/AiChat/types';
import { useClaim } from '~/components/ClaimContainer';
import type { NumberOfLines } from '~/components/core/Molecules/Collapsible/ShowAllCollapsible';
import ShowAllCollapsible from '~/components/core/Molecules/Collapsible/ShowAllCollapsible';
import Text from '~/components/core/TextComponents/Text';
import { useCurrencyFormatter } from '~/components/CurrencyFormatterContext';

import { CardLabel } from '../LabelledText';
import { TEXTS } from '../texts';
import type { ActionState } from '../types';

import {
  EXPOSURE_LIST_KEY,
  namesTemplateById,
  payloadEntries,
  payloadHeading,
  payloadLabel,
  payloadValue,
  resolveExposureList,
  TEMPLATE_KEY,
} from './utils';

interface PayloadListProps {
  action: AiAction;
  state: ActionState;
  /** A version of the action it has since replaced, which was only ever proposed. */
  isSuperseded?: boolean;
  /** Clamp each value to this many lines behind Show more. Unset, values are shown whole. */
  visibleLines?: NumberOfLines;
}

/**
 * What the action writes to the claim, as a label / value list, headed by what became of it.
 *
 * It shows what will be written, not how it got there: the fields the handler approves are the
 * fields the system executes, so the card must not ask them to approve prose alone.
 */
const PayloadList: React.FC<PayloadListProps> = ({ action, state, isSuperseded, visibleLines }) => {
  const { currencyFormatter } = useCurrencyFormatter();
  const { claim } = useClaim({ claimRequired: true });
  // Only an id is looked up: a type and name are shown as written, and no other card asks for the list.
  const { template } = useActionDocumentTemplate(action, namesTemplateById(action));
  const fields = payloadEntries(action);

  if (!fields.length) return null;

  const formatMoney = (amount: number): string => currencyFormatter.format(amount);
  const displayValue = (key: string, value: unknown): unknown => {
    if (key === EXPOSURE_LIST_KEY && typeof value === 'string') return resolveExposureList(value, claim.exposures);
    // An id the list does not resolve, or has not yet, is shown as an id, not as a bare number.
    if (key === TEMPLATE_KEY) return template?.display_name || `#${value}`;

    return value;
  };

  return (
    <div className="flex flex-col gap-8">
      <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.SEMI_BOLD} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
        {payloadHeading(state, isSuperseded)}
      </Text>
      <dl className="m-0 grid grid-cols-[minmax(120px,auto)_1fr] gap-x-16 gap-y-4">
        {fields.map(([key, value]) => (
          <React.Fragment key={key}>
            <dt className="m-0">
              <CardLabel>{payloadLabel(key)}</CardLabel>
            </dt>
            <dd className="m-0">
              {visibleLines ? (
                // The toggle is lifted over the activity card's click overlay, so it opens the value
                // rather than selecting the card.
                <ShowAllCollapsible
                  visibleLines={visibleLines}
                  showAsInlineButton
                  expandLabel={TEXTS.SHOW_MORE}
                  collapseLabel={TEXTS.SHOW_LESS}
                  linkClassName="relative z-10"
                >
                  <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                    {payloadValue(key, displayValue(key, value), formatMoney)}
                  </Text>
                </ShowAllCollapsible>
              ) : (
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                  {payloadValue(key, displayValue(key, value), formatMoney)}
                </Text>
              )}
            </dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
};

export default PayloadList;
